import { PropertyType } from "@/types/PropertyTypes";
import { ApiBase } from "./ApiBase";
import {
  AgentAlreadyLinkedToPropertyError,
  AgentInvitationAlreadyProcessedError,
  AgentInvitationExpiredError,
  AgentInvitationNotFoundError,
  LandlordCannotInviteSelfError,
} from "./Errors";

export interface GetAgentInvitationDetailsResponse {
  propertyAddress: string;
  landlordName: string;
  landlordEmail?: string;
  landlordPhoneNumber?: string;
  propertyType: PropertyType;
  propertyId: string;
}

export interface ProcessAgentInvitationRequest {
  isApproved: boolean;
}

export interface PendingManagerInvitationDto {
  reference: string;
  propertyId: string;
  propertyAddress: string;
  landlordName: string;
  createdAt: string;
  expiresAt: string;
}

export interface GetPendingManagerInvitationsResponse {
  invitations: PendingManagerInvitationDto[];
}

class AgentInvitationApi extends ApiBase {
  async getInvitationDetails(
    reference: string,
  ): Promise<GetAgentInvitationDetailsResponse> {
    return this.get<GetAgentInvitationDetailsResponse>(
      `/agent-invitations/${reference}`,
      {
        domainErrors: {
          AgentInvitationNotFound: AgentInvitationNotFoundError,
          AgentInvitationExpired: AgentInvitationExpiredError,
        },
      },
    );
  }

  async processInvitation(
    reference: string,
    request: ProcessAgentInvitationRequest,
  ): Promise<void> {
    return this.post<ProcessAgentInvitationRequest, void>(
      `/agent-invitations/${reference}/process`,
      request,
      {
        domainErrors: {
          AgentAlreadyLinkedToProperty: AgentAlreadyLinkedToPropertyError,
          AgentInvitationAlreadyProcessed: AgentInvitationAlreadyProcessedError,
          AgentInvitationExpired: AgentInvitationExpiredError,
          AgentInvitationNotFound: AgentInvitationNotFoundError,
          LandlordCannotInviteSelf: LandlordCannotInviteSelfError,
        },
      },
    );
  }

  async getPendingManagerInvitations(): Promise<GetPendingManagerInvitationsResponse> {
    return this.get<GetPendingManagerInvitationsResponse>(
      `/manager/invitations`,
    );
  }

  async generateInvitationLink(
    propertyId: string,
    invitee: {
      email?: string;
      phoneCountryCode?: string;
      phoneNumber?: string;
    },
  ): Promise<string> {
    const res = await this.post<
      {
        email?: string;
        phoneCountryCode?: string;
        phoneNumber?: string;
      },
      { invitationUri: string }
    >(`/properties/${propertyId}/agent-invitations`, invitee, {
      domainErrors: {
        LandlordCannotInviteSelf: LandlordCannotInviteSelfError,
      },
    });
    return res.invitationUri;
  }
}

export const agentInvitationApi = new AgentInvitationApi();
